// maomi/src/components/Navigation.js

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
// Material UI Icons
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SetMealIcon from '@mui/icons-material/SetMeal';
import HomeIcon from '@mui/icons-material/Home';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ExploreIcon from '@mui/icons-material/Explore';
import PublicIcon from '@mui/icons-material/Public';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ForumIcon from '@mui/icons-material/Forum';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import PersonIcon from '@mui/icons-material/Person';
import PetsIcon from '@mui/icons-material/Pets';
import EmailIcon from '@mui/icons-material/Email';
import LocalPostOfficeIcon from '@mui/icons-material/LocalPostOffice';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import NavSplide from './NavSplide';
import Register from '../pages/Register';
import Login from '../pages/Login';
import Logout from '../pages/Logout';
import Health from '../pages/Health';
import CatFoodComparison from '../pages/CatFoodComparison';
import CatFoodDetail from '../pages/CatFoodDetail';
import HelpPost from '../pages/HelpPost';
import HelpPostDetail from './HelpPostDetail';
import MyCats from '../pages/MyCats';
import CatInfo from '../pages/CatInfo';
import UserProfile from '../pages/UserProfile';
import Messages from '../pages/Messages';
import CatDetailPage from '../pages/CatDetailPage';
import CollarPage from '../pages/CollarPage';
import CollarSelectionPage from '../pages/CollarSelectionPage';
import EliminateAbuse from '../pages/EliminateAbuse';
import ScientificFeeding from '../pages/ScientificFeeding';
import NoMoreStrays from '../pages/NoMoreStrays';
import UserCenter from '../pages/UserCenter';
import UserHomepage from '../pages/UserHomepage';
import CutePetStories from '../pages/CutePetStories';
import CatForum from '../pages/CatForum';
import PetCare from '../pages/PetCare';
import CatTransportation from '../pages/CatTransportation';
import CatTravel from '../pages/CatTravel';
import CatPlanet from '../pages/CatPlanet';
import CatGuide from '../pages/CatGuide';
import ContactUs from './ContactUs';
import Logo from './Logo';
import Banner from './Banner';
import './css/Navigation.css';

function Navigation(props) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  useEffect(() => {
    const checkLoginStatus = () => {
      // Check for username in localStorage instead of token
      const storedUsername = localStorage.getItem('username');
      setIsLoggedIn(!!storedUsername);
      setUsername(storedUsername || '');
      
      // If logged in, fetch profile picture and unread message count
      if (storedUsername) {
        fetchProfilePicture();
        fetchUnreadMessageCount();
        
        // Set up polling for unread message count
        const intervalId = setInterval(fetchUnreadMessageCount, 30000); // Poll every 30 seconds
        return () => clearInterval(intervalId);
      } else {
        setProfilePicture(null);
        setUnreadMessageCount(0);
      }
    };

    const cleanup = checkLoginStatus();

    // Listen for login status changes
    window.addEventListener('loginStatusChanged', checkLoginStatus);

    return () => {
      window.removeEventListener('loginStatusChanged', checkLoginStatus);
      if (cleanup) cleanup();
    };
  }, []);
  
  const fetchUnreadMessageCount = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/messaging/messages/unread-count/`, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setUnreadMessageCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread message count:', error);
    }
  };

  const fetchProfilePicture = async () => {
    try {
      // Get the CSRF token from cookies
      const csrftoken = document.cookie.split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];
      
      const response = await fetch(`${API_BASE_URL}/api/user-profile/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-CSRFToken': csrftoken || '',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfilePicture(data.profile_picture);
      }
    } catch (error) {
      console.error('Error fetching profile picture:', error);
    }
  };

  return (
    <Router>
      <nav className="navbar navbar-expand-sm normal">
        <div className="container-fluid">
          <a className="navbar-brand" href="/">
            <span className="logo"><Logo sizeLogo='70' sizeText='70' /></span>
          </a>
          <div className="accordion navbar-collapse" id="collapsibleNavbar">
            <ul className="navbar-nav">
              <li className="nav-item">
                <Link className="nav-link" to="/help-post"><PublicIcon sx={{ mr: 1 }} />互助地图</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/cat-forum"><ForumIcon sx={{ mr: 1 }} />咪家论坛</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/cat-food-comparison"><SetMealIcon sx={{ mr: 1 }} />猫粮测评</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/cat-guide"><MenuBookIcon sx={{ mr: 1 }} />养喵指南</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/health">
                  <HealthAndSafetyIcon sx={{ mr: 1 }} />
                  <span style={{ position: 'relative', display: 'inline-block' }}>
                    AI云诊所
                    <span style={{ 
                      position: 'absolute', 
                      top: '-13px', 
                      right: '-15px', 
                      fontSize: '0.7em', 
                      color: '#ff6b6b', 
                      fontWeight: 'bold',
                      transform: 'rotate(10deg)',
                      background: '#fff5f5',
                      padding: '1px 4px',
                      borderRadius: '4px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                    }}>
                      免费
                    </span>
                  </span>
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/pet-care"><HomeIcon sx={{ mr: 1 }} />宠护到家</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/cat-transportation"><LocalShippingIcon sx={{ mr: 1 }} />猫咪托运</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/cat-travel"><ExploreIcon sx={{ mr: 1 }} />喵行天下</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/cat-planet"><NightsStayIcon sx={{ mr: 1 }} />喵星净土</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/cute-pet-stories"><NewspaperIcon sx={{ mr: 1 }} />萌宠趣闻</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/contact-us"><ContactMailIcon sx={{ mr: 1 }} />联系我们</Link>
              </li>
            </ul>
            <div className="login">
              {isLoggedIn ? (
                <div className="user-dropdown">
                  <div 
                    className="user-center-link dropdown-toggle" 
                    role="button" 
                    data-bs-toggle="dropdown"
                  >
                    <div className="user-profile-container">
                      {profilePicture ? (
                        <img 
                          src={profilePicture} 
                          alt={username} 
                          className="user-profile-pic" 
                        />
                      ) : (
                        <div className="user-profile-placeholder">
                          {username ? username.charAt(0).toUpperCase() : '?'}
                        </div>
                      )}
                      <span className="username-text">{username}</span>
                    </div>
                  </div>
                  <ul className="dropdown-menu user-dropdown-menu">
                    <li>
                      <Link className="dropdown-item" to="/user-homepage"><PersonIcon sx={{ mr: 1 }} />我的主页</Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/my-cats"><PetsIcon sx={{ mr: 1 }} />我的猫咪</Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/messages">
                        <EmailIcon sx={{ mr: 1 }} />站内消息
                        {unreadMessageCount > 0 && (
                          <span className="badge bg-danger ms-2">{unreadMessageCount}</span>
                        )}
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/user-center#shipping-address"><LocalPostOfficeIcon sx={{ mr: 1 }} />邮寄地址</Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/user-center#my-orders"><ShoppingCartIcon sx={{ mr: 1 }} />我的订单</Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/user-center#account-settings"><SettingsIcon sx={{ mr: 1 }} />账号设置</Link>
                    </li>
                    <li>
                      <Link to="/logout" className="dropdown-item"><LogoutIcon sx={{ mr: 1 }} />退出登录</Link>
                    </li>
                  </ul>
                </div>
              ) : (
                <>
                  <Link to="/login" className="custom-link">登录</Link>
                  <Link to="/register" className="custom-link">注册</Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
      <div className="main-content">
        <Routes>
          <Route index element={<NavSplide />} />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/help-post" element={<HelpPost />} />
          <Route path="/help-post/:id" element={<HelpPostDetail />} />
          <Route path="/cat-food-comparison" element={<CatFoodComparison />} />
          <Route path="/cat-food/:id" element={<CatFoodDetail />} />
          <Route path="/pet-care" element={<PetCare />} />
          <Route path="/cat-transportation" element={<CatTransportation />} />
          <Route path="/cat-travel" element={<CatTravel />} />
          <Route path="/cat-planet" element={<CatPlanet />} />
          <Route path="/cat-guide" element={<CatGuide />} />
          <Route path="/health" element={<Health />} />
          <Route path="/my-cats" element={<MyCats />} />
          <Route path="/cat/:unique_id" element={<CatInfo />} />
          <Route path="/c/:id" element={<CatDetailPage />} />
          <Route path="/collar/:sku" element={<CollarPage />} />
          <Route path="/collar-selection/:catId" element={<CollarSelectionPage />} />
          <Route path="/eliminate-abuse" element={<EliminateAbuse />} />
          <Route path="/scientific-feeding" element={<ScientificFeeding />} />
          <Route path="/no-more-strays" element={<NoMoreStrays />} />
          <Route path="/user-center" element={<UserCenter />} />
          <Route path="/user-homepage" element={<UserHomepage />} />
          <Route path="/user-profile/:userId" element={<UserProfile />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/messages/:userId" element={<Messages />} />
          <Route path="/cute-pet-stories" element={<CutePetStories />} />
          <Route path="/cat-forum" element={<CatForum />} />
          <Route path="/contact-us" element={<ContactUs />} />
        </Routes>
      </div>
      <Banner />
    </Router>
  );
}

export default Navigation;
